import AISparkleIcon from '@assets/icons/ai-sparkle.svg';
import React from 'react';
import Reanimated, {
  Extrapolation,
  interpolate,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { Platform } from 'react-native';
import { SCREEN_PADDING_SMALL, shadow } from '@/styles';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useUserContext } from '@/contexts/user-context';

const SMART_ORDER_BUTTON_SIZE = 56;
const SCROLL_DISTANCE = 150;

interface Props {
  onPress: () => void;
  scrollY: SharedValue<number>;
}

export const SmartOrderButton: React.FC<Props> = ({ onPress, scrollY }) => {
  const {
    selectedRestaurantRelationship: { smartOrderEnabled },
  } = useUserContext();
  const [styles] = useStyles();
  const isOfflineModeActive = useOfflineModeActive();

  const clampedScroll = useSharedValue(0);

  // Accumulate scroll deltas into a clamped value so the button hides when
  // scrolling down and reappears as soon as the user scrolls back up
  useAnimatedReaction(
    () => scrollY.value,
    (current, previous) => {
      if (previous === null) {
        return;
      }

      const delta = current - previous;
      clampedScroll.value = Math.max(
        0,
        Math.min(clampedScroll.value + delta, SCROLL_DISTANCE),
      );
    },
    [scrollY],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    bottom: interpolate(
      clampedScroll.value,
      [0, SCROLL_DISTANCE],
      [0, -SMART_ORDER_BUTTON_SIZE],
      Extrapolation.CLAMP,
    ),
    opacity: interpolate(
      clampedScroll.value,
      [0, SCROLL_DISTANCE],
      [1, 0],
      Extrapolation.CLAMP,
    ),
  }));

  const renderSmartOrderButton =
    smartOrderEnabled && Platform.OS !== 'web' && !isOfflineModeActive;

  return renderSmartOrderButton ? (
    <Reanimated.View style={animatedStyle}>
      <Button
        containerStyle={styles.base}
        Icon={AISparkleIcon}
        onPress={onPress}
        rounded
        style={styles.button}
        testID="smart-order-button"
        trackingData={{
          name: 'smart_order_button',
        }}
        type="tertiary"
      />
    </Reanimated.View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  base: {
    bottom: SCREEN_PADDING_SMALL,
    left: SCREEN_PADDING_SMALL,
    position: 'absolute',
  },
  button: {
    ...shadow({
      elevation: 2,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 4,
    }),
    backgroundColor: theme.colors.background,
    height: SMART_ORDER_BUTTON_SIZE,
    width: SMART_ORDER_BUTTON_SIZE,
  },
}));
